/**
 * Application Entry Point
 *
 * P2-1-3: Sentry error tracking integration
 * - Initializes Sentry for production error monitoring
 * - Captures unhandled errors with context
 */

import { createRoot } from "react-dom/client";
import * as Sentry from "@sentry/react";
import App from "./App.tsx";
import "./index.css";

// Initialize Sentry (production only)
const sentryDsn = import.meta.env.VITE_SENTRY_DSN;
if (import.meta.env.PROD && sentryDsn) {
  Sentry.init({
    dsn: sentryDsn,
    environment: import.meta.env.MODE,

    // Performance Monitoring
    tracesSampleRate: 0.1, // 10% of transactions

    // Session Replay (optional, can be increased for debugging)
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,

    // Integration settings
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Filter out noisy errors
    ignoreErrors: [
      // Network errors that are expected
      "ResizeObserver loop limit exceeded",
      "ResizeObserver loop completed with undelivered notifications",
      // User cancelled operations
      "AbortError",
      "The user aborted a request",
    ],

    // Add tags for better filtering
    initialScope: {
      tags: {
        app: "jarvis-lms",
        version: import.meta.env.VITE_APP_VERSION || "unknown",
      },
    },
  });
}

createRoot(document.getElementById("root")!).render(<App />);

// Register Service Worker (production only for PWA support)
// P3-4: Use BASE_URL to support non-root deployments (e.g., /staging/)
if (import.meta.env.PROD && "serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    const swPath = `${import.meta.env.BASE_URL}sw.js`;
    navigator.serviceWorker
      .register(swPath)
      .then((registration) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log("SW registered: ", registration.scope);
        }
      })
      .catch((error) => {
        if (import.meta.env.DEV) {
          // eslint-disable-next-line no-console
          console.log("SW registration failed: ", error);
        }
      });
  });
}
